import { t } from "@hakili/i18n";
import { ERROR_TEXT, METADONNEE, TEXTE_SECONDAIRE, TEXTE_TERTIAIRE } from "@hakili/ui";
import { useState } from "react";

import type { VehiclePhoto } from "../session/useSession.js";
import { Car } from "lucide-react";

/**
 * `round F2`, `F2.3`: **the gallery on a car's page**, as
 * `hakili-dealer.jsx:1653-1700` draws it , a hero, its view's name on it, a
 * counter, and a strip of thumbnails each labelled with the view it shows.
 *
 * ## Why the view's NAME is on every thumbnail, and not only in a tooltip
 *
 * The drawing labels each one , « Avant droit », « Profil droit », « Arrière »
 * , and it is not decoration. A buyer three hundred kilometres away judges a
 * car on these photographs, and the seller's own question while taking them is
 * *which one have I not done*. A strip of unlabelled thumbnails answers neither.
 *
 * ## « Retirer », R's last review of the strip
 *
 * The caption is the kit's metadata colour at 13 px (it was grey at 10 px, 2,5:1). « Retirer » is the kit's quiet danger style: the kit's error text, a visible edge and a 44 px target; the page asks before it writes
 * (`ConfirmDialog`), and the door it calls is the same one.
 *
 * ## « +N »
 *
 * The drawing shows seven thumbnails and then a count. A gallery of twelve in
 * an eighty-pixel-tall strip is twelve pictures nobody can tell apart; the
 * eighth cell says how many more there are and opens them.
 */

/** How many thumbnails the strip shows before it counts the rest. */
const STRIP = 7;

export interface GalleryProps {
  readonly photos: readonly VehiclePhoto[];
  /** The recommended views this car still lacks, from the door. */
  readonly missingRecommended: readonly string[];
  /** Present only where the caller may remove one , the panel, not the page. */
  readonly onRemove?: ((photoId: string) => void) | undefined;
}

export function Gallery({ photos, missingRecommended, onRemove }: GalleryProps): React.JSX.Element {
  const [openAll, setOpenAll] = useState(false);
  const [shown, setShown] = useState(0);
  const hero = photos[shown] ?? photos[0];
  const strip = openAll ? photos : photos.slice(0, STRIP);
  const rest = photos.length - strip.length;

  if (photos.length === 0) {
    return (
      // In WORDS, and with the drawing's own placeholder above them: an empty
      // frame reads as an image that failed to load, which is a different fact
      // from a car nobody has photographed yet.
      <div
        className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-6 py-10 text-center"
        data-testid="galerie-vide"
      >
        <span className="text-gray-300">
          <Car className="h-6 w-6 text-gray-300" aria-hidden="true" />
        </span>
        <p className={`text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.vehicules.galerieVide")}</p>
      </div>
    );
  }

  return (
    <div data-testid="galerie">
      <div className="relative h-64 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
        {/**
         * **The hero shows the WHOLE photograph, and the frame is filled behind
         * it.** The founder's decision of 7 September 2026, after the first real
         * photographs reached the bucket from staging , five cars, `V-004` with
         * five named views , and both texts are kept.
         *
         * The note that stood here said the hero FILLS its frame, and its
         * reason was true as far as it went: `object-contain` alone letterboxes,
         * and a wall of grey bands is what a catalogue of phone photographs
         * would have looked like. What the one-pixel stand-in could not show is
         * what a real photograph does: `object-cover` CROPS, and it crops the
         * roof off a saloon shot in portrait, which is the one thing a big
         * picture exists to prevent.
         *
         * So both are used, and each does what it is good at. Behind: the same
         * photograph, enlarged, blurred and darkened a little , it fills the
         * frame, keeps its shape, and shows no bar. In front: the whole
         * photograph, contained and centred, cropped nowhere.
         *
         * The law booked beside `6.8`: **a big visual exists to be seen whole;
         * a crop is for thumbnails.** The strip below and the card's photograph
         * keep `object-cover` for exactly that reason , a thumbnail is a
         * pointer, not a picture.
         */}
        <img
          src={hero?.url ?? ""}
          alt=""
          aria-hidden="true"
          onError={(event) => {
            event.currentTarget.style.visibility = "hidden";
          }}
          className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-xl brightness-90"
          data-testid="galerie-hero-fond"
        />
        <img
          onError={(event) => {
            // `round F3`: the object is gone or the browser would not read it,
            // and either way the frame must not stay broken. The drawing's own
            // grey block is what a car with no photograph shows, and a
            // photograph nobody can load is that, from a reader's seat.
            event.currentTarget.style.visibility = "hidden";
          }}
          src={hero?.url ?? ""}
          alt={hero?.view ?? ""}
          className="relative h-64 w-full object-contain object-center"
          data-testid="galerie-hero"
        />
        {/* The view's name ON the hero, as the drawing has it: a person looking
          at a wheel arch needs to be told which one it is. */}
        <span
          className={`absolute top-3 left-3 rounded-lg bg-white/90 px-2.5 py-1 ${METADONNEE} font-medium text-gray-700`}
          data-testid="galerie-vue"
        >
          {hero?.view ?? ""}
        </span>
        <span
          className={`absolute right-3 bottom-3 rounded-full bg-white/90 px-2.5 py-1 tabular-nums ${METADONNEE} ${TEXTE_SECONDAIRE}`}
          data-testid="galerie-compteur"
        >
          {String(shown + 1)} {t("dealer.vehicules.sur")} {String(photos.length)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="galerie-vignettes">
        {strip.map((photo, index) => (
          <div key={photo.id} className="w-20">
            <button
              type="button"
              onClick={() => {
                setShown(index);
              }}
              className={`block h-14 w-20 overflow-hidden rounded-lg border ${
                index === shown ? "border-indigo-400 ring-1 ring-indigo-100" : "border-gray-200"
              }`}
              data-testid={`galerie-vignette-${photo.id}`}
            >
              <img
                src={photo.url}
                alt={photo.view ?? ""}
                className="h-full w-full object-cover object-center"
                onError={(event) => {
                  // As the hero: a thumbnail nobody can load shows the frame's
                  // own grey rather than a broken image and its alt text.
                  event.currentTarget.style.visibility = "hidden";
                }}
              />
            </button>
            {/* The name under each, in the drawing's own small type. */}
            <span
              className="mt-1 block truncate text-center text-[13px] text-[color:var(--hk-text-2)]"
              data-testid={`galerie-legende-${photo.id}`}
            >
              {photo.view}
            </span>
            {onRemove === undefined ? null : (
              <button
                type="button"
                onClick={() => {
                  onRemove(photo.id);
                }}
                className={`mt-1 block min-h-[44px] w-full rounded-lg border border-[color:var(--hk-line-strong)] bg-white text-center text-[13px] font-medium ${ERROR_TEXT}`}
                aria-label={
                  photo.view === null
                    ? t("dealer.vehicules.retirerPhoto")
                    : t("dealer.vehicules.retirerPhotoNom").split("{legende}").join(photo.view)
                }
                data-testid={`galerie-retirer-${photo.id}`}
              >
                {t("dealer.vehicules.retirerPhoto")}
              </button>
            )}
          </div>
        ))}
        {rest <= 0 ? null : (
          <button
            type="button"
            onClick={() => {
              setOpenAll(true);
            }}
            className={`h-14 w-20 rounded-lg border border-gray-200 bg-gray-50 ${METADONNEE} ${TEXTE_SECONDAIRE}`}
            data-testid="galerie-plus"
          >
            +{String(rest)}
          </button>
        )}
      </div>

      {missingRecommended.length === 0 ? null : (
        // The drawing's own sentence, and it is only honest because the list
        // behind it is derived from what the car actually holds.
        <p className={`mt-3 ${METADONNEE} ${TEXTE_TERTIAIRE}`} data-testid="galerie-manquantes">
          {t("dealer.vehicules.vuesManquantes")} {missingRecommended.join(", ").toLocaleLowerCase("fr-FR")}
          {". "}
          {t("dealer.vehicules.vuesManquantesRaison")}
        </p>
      )}
    </div>
  );
}
